import { Extension } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';
import { POST_EDITOR_MEDIA_CAPTION_NODE_TYPES } from '../lib/post_editor_constants.js';

export const CaptionExtension = Extension.create({
  name: 'caption',

  addOptions() {
    return {
      types: ['image', 'video'], // Node types that support captions
      placeholder: 'Enter caption...',
      className: 'caption-container',
    };
  },

  addCommands() {
    return {
      focusCaption: (nodeType) => ({ editor, state }) => {
        const { selection } = state;
        let node = selection.node;
        let nodePos = null;

        // Find the target node
        if (!node && selection.$head && selection.$head.parent) {
          node = selection.$head.parent;
          nodePos = selection.$head.start() - 1;
        }

        // Walk up the tree to find the target node type
        if (!node || node.type.name !== nodeType) {
          for (let depth = selection.$head.depth; depth >= 0; depth--) {
            const nodeAtDepth = selection.$head.node(depth);
            if (nodeAtDepth.type.name === nodeType) {
              node = nodeAtDepth;
              nodePos = selection.$head.start(depth) - 1;
              break;
            }
          }
        }

        if (node && this.options.types.includes(node.type.name)) {
          if (nodePos !== null) {
            const captionPos = nodePos + 1;

            // Insert zero-width space if caption is empty
            if (node.childCount === 0) {
              editor.chain().focus().command(({ tr, dispatch }) => {
                tr.insert(captionPos, state.schema.text('\u200B'));
                dispatch(tr);
                return true;
              }).run();
            }

            // Focus into the caption
            editor.chain().focus().setTextSelection({ from: captionPos }).run();
            return true;
          }
        }

        return false;
      },
    };
  },
});

// Helper function to create caption DOM structure
export const createCaptionDOM = (className = 'caption-container', options = {}) => {
  const wrapper = document.createElement('div');
  wrapper.className = `${className}-wrapper`;

  const container = document.createElement('figcaption');
  // Media captions are always styled via the class-based Post typography contract.
  // Keep the hook className (image-caption-container/video-caption-container) for
  // editor behaviors, but do not apply inline typography.
  container.className = `${className} ps-post-style-caption`;
  container.setAttribute('data-tiptap-content', '');

  wrapper.appendChild(container);
  return { wrapper, container };
};

// Helper function to handle caption keyboard events
export const setupCaptionKeyboardHandling = (container, editor) => {
  // Handle clicks on empty captions to prevent breaking list structure
  container.addEventListener('click', (e) => {
    const { state, selection } = editor;
    const { $from } = selection;
    
    // Check if we're in a caption (inside image or video node)
    let imageOrVideoNode = null;
    let imageOrVideoPos = null;
    
    // Walk up the tree to find image/video/embedVideo node
    for (let depth = $from.depth; depth >= 0; depth--) {
      const nodeAtDepth = $from.node(depth);
      if (POST_EDITOR_MEDIA_CAPTION_NODE_TYPES.includes(nodeAtDepth.type.name)) {
        imageOrVideoNode = nodeAtDepth;
        imageOrVideoPos = $from.start(depth);
        break;
      }
    }
    
    if (!imageOrVideoNode || imageOrVideoPos === null) {
      return; // Not in a caption, let default behavior happen
    }
    
    // Check if caption is empty
    const currentNode = $from.node($from.depth);
    const captionText = currentNode.textContent || '';
    const isEmpty = currentNode.content.size === 0 || 
                    (currentNode.content.size <= 2 && captionText.trim() === '');
    
    if (isEmpty) {
      // Prevent default selection behavior that might break the list
      e.preventDefault();
      e.stopPropagation();
      
      // Focus the caption and insert zero-width space if needed
      const captionPos = imageOrVideoPos + 1;
      
      editor.chain().focus().command(({ tr, dispatch }) => {
        if (!dispatch) return false;
        
        // Check if we're inside a list item
        let listItemPos = null;
        let listItemNode = null;
        
        const imageOrVideoResolved = state.doc.resolve(imageOrVideoPos);
        for (let depth = imageOrVideoResolved.depth; depth >= 0; depth--) {
          const nodeAtDepth = imageOrVideoResolved.node(depth);
          if (nodeAtDepth.type.name === 'listItem') {
            listItemPos = imageOrVideoResolved.start(depth);
            listItemNode = nodeAtDepth;
            break;
          }
        }
        
        // If inside a list, ensure we don't break it
        if (listItemNode && listItemPos !== null) {
          // Just insert zero-width space and focus - don't modify list structure
          const existingNode = tr.doc.nodeAt(captionPos);
          if (!existingNode || existingNode.content.size === 0) {
            tr.insert(captionPos, state.schema.text('\u200B'));
          }
          const resolvedPos = tr.doc.resolve(captionPos + 1);
          tr.setSelection(TextSelection.near(resolvedPos));
        } else {
          // Not in a list - standard behavior
          const existingNode = tr.doc.nodeAt(captionPos);
          if (!existingNode || existingNode.content.size === 0) {
            tr.insert(captionPos, state.schema.text('\u200B'));
          }
          const resolvedPos = tr.doc.resolve(captionPos + 1);
          tr.setSelection(TextSelection.near(resolvedPos));
        }
        
        tr.scrollIntoView();
        return true;
      }).run();
    }
  });
  
  // Enter key handling is now done via ProseMirror keyboard shortcuts in image/video extensions
  // This is more reliable for preserving list structure
};
